import { ref } from 'vue';
import { useArloService } from '@/services/arloapi.js';
import { useErrorHandler } from '@/composables/useErrorHandler.js';
import { financeiroEndpoint } from '@/api/endpoints/financeiro';
import { basicosEndpoint } from '@/api/endpoints/basicos';
import { dashboardEndpoint } from '@/api/endpoints/dashboards';

export function useFinanceiroData() {
    const { getDados } = useArloService();
    const { handleApiError } = useErrorHandler();

    const error = ref(null);

    const listaPagamentosAutorizados = ref([]);
    const listaPagamentosEmProcessamento = ref([]);
    const listaPagamentosConfirmados = ref({ items: [], total: 0 });
    const listaPagamentosComErros = ref([]);
    const listaPagamentosEncargos = ref([]);
    const listaPagamentosPausados = ref([]);
    const listaPagamentosPendentes = ref([]);
    const listaPagamentosCancelados = ref({ items: [], total: 0 });
    const listaPagamentosPorIdentificacao = ref({ items: [], total: 0 });
    const listaPagamentos = ref([]);
    const listaEncargos = ref([]);
    const listaDashboardFinanceiro = ref([]);
    const simuladorEncargos = ref(null);
    const pagamento = ref({});

    const carregarPagamento = async (id) => {
        try {
            const response = await getDados(financeiroEndpoint.pagamentos.obter(id));
            pagamento.value = response.value;
        } catch (err) {
            handleApiError(err);
            throw err;
        }
    }

    const carregarPagamentosAutorizados = async (page = 1, size = 100, filtros = {}) => {
        try {
            let url = financeiroEndpoint.pagamentos.listar + '?status=' + import.meta.env.VITE_STATUS_PAGAMENTO_AUTORIZADO + ',' + import.meta.env.VITE_STATUS_PAGAMENTO_REABILITADO + '&page=' + page + '&size=' + size;

            // Adiciona filtros opcionais à URL
            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.valor_min != null) {
                url += `&valor_min=${parseFloat(filtros.valor_min)}`;
            }
            if (filtros.valor_max != null) {
                url += `&valor_max=${parseFloat(filtros.valor_max)}`;
            }
            if (filtros.beneficiario) {
                url += `&beneficiario=${filtros.beneficiario}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            if (filtros.tipo_solicitacao) {
                url += `&tipo_solicitacao=${encodeURIComponent(filtros.tipo_solicitacao)}`;
            }
            if (filtros.pagamentolabel_id) {
                url += `&pagamentolabel_id=${encodeURIComponent(filtros.pagamentolabel_id)}`;
            }
            if (filtros.projeto_id) {
                url += `&projeto_id=${filtros.projeto_id}`;
            }

            const response = await getDados(url);

            listaPagamentosAutorizados.value = response.value
            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarPagamentosPausados = async (page = 1, size = 100, filtros = {}) => {
        try {
            let url = financeiroEndpoint.pagamentos.listar + '?status=' + import.meta.env.VITE_STATUS_PAGAMENTO_PAUSADO + '&page=' + page + '&size=' + size;
            // Adiciona filtros opcionais à URL
            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.valor_min != null) {
                url += `&valor_min=${parseFloat(filtros.valor_min)}`;
            }
            if (filtros.valor_max != null) {
                url += `&valor_max=${parseFloat(filtros.valor_max)}`;
            }
            if (filtros.beneficiario) {
                url += `&beneficiario=${filtros.beneficiario}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            if (filtros.tipo_solicitacao) {
                url += `&tipo_solicitacao=${encodeURIComponent(filtros.tipo_solicitacao)}`;
            }if (filtros.pagamentolabel_id) {
                url += `&pagamentolabel_id=${encodeURIComponent(filtros.pagamentolabel_id)}`;
            }
            if (filtros.projeto_id) {
                url += `&projeto_id=${filtros.projeto_id}`;
            }

            const response = await getDados(url);
            listaPagamentosPausados.value = response.value
            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarPagamentosEmProcessamento = async (page = 1, size = 100, filtros = {}) => {
        try {
            let url = financeiroEndpoint.pagamentos.listar + '?status=' + import.meta.env.VITE_STATUS_PAGAMENTO_EM_PROCESSAMENTO + '&page=' + page + '&size=' + size;

            // Adiciona filtros opcionais à URL
            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.valor_min != null) {
                url += `&valor_min=${parseFloat(filtros.valor_min)}`;
            }
            if (filtros.valor_max != null) {
                url += `&valor_max=${parseFloat(filtros.valor_max)}`;
            }
            if (filtros.beneficiario) {
                url += `&beneficiario=${filtros.beneficiario}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            if (filtros.tipo_solicitacao) {
                url += `&tipo_solicitacao=${encodeURIComponent(filtros.tipo_solicitacao)}`;
            }if (filtros.pagamentolabel_id) {
                url += `&pagamentolabel_id=${encodeURIComponent(filtros.pagamentolabel_id)}`;
            }
            if (filtros.projeto_id) {
                url += `&projeto_id=${filtros.projeto_id}`;
            }

            const response = await getDados(url);

            listaPagamentosEmProcessamento.value = response.value

            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarPagamentosConfirmados = async (page = 1, size = 100, filtros = {}) => {
        try {
            let url = financeiroEndpoint.pagamentos.listar + '?status=' + import.meta.env.VITE_STATUS_PAGAMENTO_CONFIRMADO + '&page=' + page + '&size=' + size;

            // Adiciona filtros opcionais à URL
            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.valor_min != null) {
                url += `&valor_min=${parseFloat(filtros.valor_min)}`;
            }
            if (filtros.valor_max != null) {
                url += `&valor_max=${parseFloat(filtros.valor_max)}`;
            }
            if (filtros.beneficiario) {
                url += `&beneficiario=${filtros.beneficiario}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            if (filtros.tipo_solicitacao) {
                url += `&tipo_solicitacao=${encodeURIComponent(filtros.tipo_solicitacao)}`;
            }if (filtros.pagamentolabel_id) {
                url += `&pagamentolabel_id=${encodeURIComponent(filtros.pagamentolabel_id)}`;
            }
            if (filtros.projeto_id) {
                url += `&projeto_id=${filtros.projeto_id}`;
            }

            const response = await getDados(url);

            listaPagamentosConfirmados.value = response.value

            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarPagamentosPorIdentificacao = async (identificacao, page = 1, size = 20) => {
        try {
            // A busca aceita CPF/CNPJ com ou sem pontuação; a API espera só os dígitos.
            const somenteDigitos = String(identificacao || '').replace(/\D/g, '');

            if (!somenteDigitos) {
                listaPagamentosPorIdentificacao.value = { items: [], total: 0 };
                return listaPagamentosPorIdentificacao.value;
            }

            const url = `${financeiroEndpoint.pagamentos.listarPorIdentificacao}?identificacao=${somenteDigitos}&page=${page}&size=${size}`;
            const response = await getDados(url);

            listaPagamentosPorIdentificacao.value = response.value ?? { items: [], total: 0 };

            return response;
        } catch (err) {
            listaPagamentosPorIdentificacao.value = { items: [], total: 0 };
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarPagamentosComErros = async (page = 1, size = 100, filtros = {}) => {
        try {
            let url = financeiroEndpoint.pagamentos.listar + '?status=' + import.meta.env.VITE_STATUS_PAGAMENTO_REJEITADO_COM_ERRO + '&page=' + page + '&size=' + size;

            // Adiciona filtros opcionais à URL
            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.valor_min != null) {
                url += `&valor_min=${parseFloat(filtros.valor_min)}`;
            }
            if (filtros.valor_max != null) {
                url += `&valor_max=${parseFloat(filtros.valor_max)}`;
            }
            if (filtros.beneficiario) {
                url += `&beneficiario=${filtros.beneficiario}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            if (filtros.tipo_solicitacao) {
                url += `&tipo_solicitacao=${encodeURIComponent(filtros.tipo_solicitacao)}`;
            }if (filtros.pagamentolabel_id) {
                url += `&pagamentolabel_id=${encodeURIComponent(filtros.pagamentolabel_id)}`;
            }
            if (filtros.projeto_id) {
                url += `&projeto_id=${filtros.projeto_id}`;
            }

            const response = await getDados(url);

            listaPagamentosComErros.value = response.value

            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    // Filtros: situacao (vigentes | canceladas | todas), mes_referencia (AAAA-MM), tipo_esfera_id,
    // projeto_id e lote_id. Os vazios ficam fora da URL, e sem situacao a API devolve as vigentes.
    const carregarPagamentosEncargos = async (filtros = {}) => {
        try {
            const params = new URLSearchParams();
            Object.entries(filtros).forEach(([chave, valor]) => {
                if (valor !== null && valor !== undefined && valor !== '') params.append(chave, valor);
            });

            const query = params.toString();
            const url = financeiroEndpoint.solicitacoesPagamentosEncargos.listar;
            const response = await getDados(query ? `${url}?${query}` : url);
            listaPagamentosEncargos.value = response.value
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const listaPagamentosEncargosPorProjeto = ref([]);

    const carregarPagamentosEncargosPorProjeto = async ({ mes_referencia, projeto_id, tipos_encargos_ids } = {}) => {
        try {
            const params = new URLSearchParams();
            if (mes_referencia) params.append('mes_referencia', mes_referencia);
            if (projeto_id) params.append('projeto_id', projeto_id);
            (tipos_encargos_ids || []).forEach((id) => params.append('tipos_encargos_ids', id));

            const response = await getDados(`${financeiroEndpoint.pagamentosEncargos.listarPorProjeto}?${params.toString()}`);
            listaPagamentosEncargosPorProjeto.value = response.value || [];
            return response;
        } catch (err) {
            // Carregamento reativo (disparado por watch): não deve redirecionar/sair da página em erro.
            error.value = handleApiError(err, { redirectOnError: false });
            throw err;
        }
    }

    const carregarPagamentosPendentes = async (page = 1, size = 100, filtros = {}) => {
        try {
            let url = financeiroEndpoint.pagamentos.listar + '?status=' + import.meta.env.VITE_STATUS_PAGAMENTO_PENDENTE + '&page=' + page + '&size=' + size;

            // Adiciona filtros opcionais à URL
            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.valor_min != null) {
                url += `&valor_min=${parseFloat(filtros.valor_min)}`;
            }
            if (filtros.valor_max != null) {
                url += `&valor_max=${parseFloat(filtros.valor_max)}`;
            }
            if (filtros.beneficiario) {
                url += `&beneficiario=${filtros.beneficiario}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            if (filtros.tipo_solicitacao) {
                url += `&tipo_solicitacao=${encodeURIComponent(filtros.tipo_solicitacao)}`;
            }if (filtros.pagamentolabel_id) {
                url += `&pagamentolabel_id=${encodeURIComponent(filtros.pagamentolabel_id)}`;
            }
            if (filtros.projeto_id) {
                url += `&projeto_id=${filtros.projeto_id}`;
            }
            // `false` é um valor válido aqui, então não pode cair no `if` truthy
            // dos filtros acima.
            if (filtros.incluir_solicitacao_administrativa !== undefined) {
                url += `&incluir_solicitacao_administrativa=${filtros.incluir_solicitacao_administrativa}`;
            }

            const response = await getDados(url);

            listaPagamentosPendentes.value = response.value

            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarPagamentosCancelados = async (page = 1, size = 100, filtros = {}) => {
        try {
            let url = financeiroEndpoint.pagamentos.listar + '?status=' + import.meta.env.VITE_STATUS_PAGAMENTO_CANCELADO + '&page=' + page + '&size=' + size;

            // Adiciona filtros opcionais à URL
            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.valor_min != null) {
                url += `&valor_min=${parseFloat(filtros.valor_min)}`;
            }
            if (filtros.valor_max != null) {
                url += `&valor_max=${parseFloat(filtros.valor_max)}`;
            }
            if (filtros.beneficiario) {
                url += `&beneficiario=${filtros.beneficiario}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            if (filtros.tipo_solicitacao) {
                url += `&tipo_solicitacao=${encodeURIComponent(filtros.tipo_solicitacao)}`;
            }if (filtros.pagamentolabel_id) {
                url += `&pagamentolabel_id=${encodeURIComponent(filtros.pagamentolabel_id)}`;
            }
            if (filtros.projeto_id) {
                url += `&projeto_id=${filtros.projeto_id}`;
            }

            const response = await getDados(url);

            listaPagamentosCancelados.value = response.value

            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarEncargos = async (filtros = {}) => {
        try {
            const params = new URLSearchParams();

            // Competência é obrigatória; a esfera é opcional — sem ela a API traz as três.
            if (filtros.competencia) {
                params.append('competencia', filtros.competencia);
            }

            if (filtros.tipoesfera_id != null) {
                params.append('tipoesfera_id', filtros.tipoesfera_id);
            }

            const query = params.toString();
            const url = query ? `${financeiroEndpoint.encargos.listar}?${query}` : financeiroEndpoint.encargos.listar;

            const response = await getDados(url);

            listaEncargos.value = response.value

            return response;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarDashboardFinanceiro = async () => {
        try {
            const response = await getDados(dashboardEndpoint.analistaFinanceiro);
            listaDashboardFinanceiro.value = response.value;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarCalcularEncargos = async (valorBruto, competencia, calculaIss) => {
        try {
            const url = `${financeiroEndpoint.encargos.calcularInssAutonomo}?valor_bruto=${valorBruto}&competencia=${competencia}&calcula_iss=${calculaIss}`;
            const response = await getDados(url);
            simuladorEncargos.value = response.value;
        } catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }

    const carregarPorStatus = async (status, page = 1, size = 100, filtros = {}) => {
        try {
            let url = `${financeiroEndpoint.pagamentos.listar}?status=${status}&page=${page}&size=${size}`;

            if (filtros.data_inicio) {
                url += `&data_inicio=${filtros.data_inicio}`;
            }
            if (filtros.data_fim) {
                url += `&data_fim=${filtros.data_fim}`;
            }
            if (filtros.data_vencimento_inicio) {
                url += `&data_vencimento_inicio=${filtros.data_vencimento_inicio}`;
            }
            if (filtros.data_vencimento_fim) {
                url += `&data_vencimento_fim=${filtros.data_vencimento_fim}`;
            }
            if (filtros.search) {
                url += `&search=${encodeURIComponent(filtros.search)}`;
            }
            // `false` é um valor válido aqui, então não pode cair no `if` truthy
            // dos filtros acima.
            if (filtros.incluir_solicitacao_administrativa !== undefined) {
                url += `&incluir_solicitacao_administrativa=${filtros.incluir_solicitacao_administrativa}`;
            }

            const response = await getDados(url);
            listaPagamentos.value = response.value;
            return response;
        }
        catch (err) {
            error.value = handleApiError(err);
            throw err;
        }
    }
    return {
        error,

        listaPagamentosAutorizados,
        listaPagamentosEmProcessamento,
        listaPagamentosConfirmados,
        listaPagamentosComErros,
        listaPagamentosEncargos,
        listaPagamentosEncargosPorProjeto,
        listaPagamentosPendentes,
        listaEncargos,
        listaPagamentosPausados,
        listaPagamentosCancelados,
        listaPagamentosPorIdentificacao,
        listaDashboardFinanceiro,
        simuladorEncargos,
        listaPagamentos,
        pagamento,

        carregarPorStatus,
        carregarPagamentosEncargos,
        carregarPagamentosEncargosPorProjeto,
        carregarPagamentosAutorizados,
        carregarPagamentosEmProcessamento,
        carregarPagamentosConfirmados,
        carregarPagamentosPorIdentificacao,
        carregarPagamentosComErros,
        carregarPagamentosPendentes,
        carregarEncargos,
        carregarPagamentosPausados,
        carregarPagamentosCancelados,
        carregarDashboardFinanceiro,
        carregarCalcularEncargos,
        carregarPagamento
    }
}

const listaLabelsPagamentos = ref([]);

export function useBasicosFinanceiro() {
    const { getDados } = useArloService();

    const listaTiposSolicitacoesFinanceiro = ref([]);
    const listaSolicitacoesFinanceiro = ref({ items: [], total: 0 });
    const listaTiposSolicitacoes = ref([]);
    const listaTipoEncargos = ref([]);

    const carregarTipoEncargos = async () => {
        const response = await getDados(financeiroEndpoint.encargos.listarTipos);
        listaTipoEncargos.value = response.value;
    }

    const carregarTiposSolicitacoesFinanceiro = async () => {
        const response = await getDados(basicosEndpoint.tipoSolicitacoesFinanceiro.listar);
        listaTiposSolicitacoesFinanceiro.value = response.value;
    }

    // filtros: { projeto_id, tipo_solicitacao_financeiro_id }; os vazios ficam fora da URL
    const carregarSolicitacoesFinanceiro = async (page = 1, size = 10, avaliadas = false, search = '', filtros = {}) => {
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const filtrosParam = Object.entries(filtros)
            .filter(([, valor]) => valor)
            .map(([chave, valor]) => `&${chave}=${valor}`)
            .join('');
        await getDados(`${financeiroEndpoint.solicitacoesFinanceiro.listar}?avaliadas=${avaliadas}&page=${page}&size=${size}${searchParam}${filtrosParam}`).then((response) => {
            listaSolicitacoesFinanceiro.value = response.value;
        });
    }

    const carregarTiposSolicitacoes = async () => {
        const response = await getDados(basicosEndpoint.tiposSolicitacoes.listar);
        listaTiposSolicitacoes.value = response.value;
    }

    const carregarLabelsPagamentos = async () => {
        const response = await getDados(financeiroEndpoint.labels.listar);
        listaLabelsPagamentos.value = response.value;
    }

    return {
        listaTiposSolicitacoesFinanceiro,
        listaSolicitacoesFinanceiro,
        listaTiposSolicitacoes,
        listaLabelsPagamentos,
        listaTipoEncargos,

        carregarTiposSolicitacoesFinanceiro,
        carregarSolicitacoesFinanceiro,
        carregarTiposSolicitacoes,
        carregarLabelsPagamentos,
        carregarTipoEncargos,
    }
}